import React, { useState, useEffect, useRef } from "react";
import SbEditable from "storyblok-react";
import FluidImage from "src/utils/FluidImage";
import RichText from "src/utils/RichText";
import UpArrowOrange from "static/up-arrow-orange.svg";
import DownArrowNavy from "static/down-arrow-navy.png";
import OrangeSmile from "static/orange-smile.png";
import ThinWave from "static/thin-wave-2.png";
import MobileWave from "static/mobile-brush.png";

const AccordionAndImage = ({ blok }) => {
  const [selected, setSelected] = useState(null);
  const bulletRefs = useRef([]);
  
  // Refs for Intersection Observer
  const leftColumnRef = useRef(null);
  const rightColumnRef = useRef(null);
  const [leftVisible, setLeftVisible] = useState(false);
  const [rightVisible, setRightVisible] = useState(false);

  useEffect(() => {
    if (selected !== null) {
      bulletRefs.current[selected]?.forEach((el, index) => {
        if (el) {
          setTimeout(() => {
            el.classList.add("opacity-100", "translate-x-0");
            el.classList.remove("opacity-0", "translate-x-10");
          }, index * 150);
        }
      });
    } else {
      bulletRefs.current.forEach((bulletList) => {
        bulletList?.forEach((el) => {
          el?.classList.remove("opacity-100", "translate-x-0");
          el?.classList.add("opacity-0", "translate-x-10");
        });
      });
    }
  }, [selected]);

  useEffect(() => {
    const observerOptions = { root: null, threshold: 0.2 };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === leftColumnRef.current) setLeftVisible(entry.isIntersecting);
        if (entry.target === rightColumnRef.current) setRightVisible(entry.isIntersecting);
      });
    }, observerOptions);

    if (leftColumnRef.current) observer.observe(leftColumnRef.current);
    if (rightColumnRef.current) observer.observe(rightColumnRef.current);

    return () => {
      if (leftColumnRef.current) observer.unobserve(leftColumnRef.current);
      if (rightColumnRef.current) observer.unobserve(rightColumnRef.current);
    };
  }, []);

  const toggleAccordion = (index) => {
    setSelected(selected === index ? null : index);
  };

  return (
    <SbEditable content={blok} key={blok._uid}>
      <div id="we-supercharge-brands" className="relative bg-white overflow-hidden">
        {/* Background Images */}
        <div className="absolute top-0 hidden lg:block lg:translate-x-1/4">
          <img src={ThinWave} alt="backgroundwave" />
        </div>
        <div className="absolute lg:hidden top-0">
          <img src={MobileWave} alt="backgroundwave" />
        </div>

        <div className="container mx-auto px-6 lg:py-24 py-12 flex lg:flex-row flex-col gap-14 lg:gap-0">
          {/* Image (Left Column) */}
          <div
            ref={leftColumnRef}
            className={`w-full lg:w-1/2 order-2 lg:order-1 relative transform transition-all duration-1000 ease-out ${
              leftVisible ? "translate-x-0 opacity-100" : "-translate-x-20 opacity-0"
            }`}
          >
            <FluidImage image={blok.image} className="w-full h-full object-cover relative" />
            {/* Orange Smile */}
            <div className="absolute bottom-0 right-0 -translate-x-1/4 translate-y-1/2">
              <img src={OrangeSmile} alt="Orange Smile" className="w-16 h-16 lg:w-32 lg:h-32" />
            </div>
          </div>

          {/* Content (Right Column) */}
          <div
            ref={rightColumnRef}
            className={`relative w-full lg:w-1/2 order-1 lg:order-2 transform transition-all duration-1000 ease-out ${
              rightVisible ? "translate-x-0 opacity-100" : "translate-x-20 opacity-0"
            }`}
          >
            <div className="lg:ml-auto max-w-xl w-full">
              {/* Title and Content */}
              <div className="mb-10 lg:mb-14">
                <div className="[&_p_b]:text-orange [&_p_b]:uppercase h2 text-midnight mb-3">
                  <RichText>{blok.title}</RichText>
                </div>
                <div className="body-text text-midnight">{blok.content}</div>
              </div>

              {/* Accordion */}
              <ul className="block my-4 lg:my-0 mx-auto">
                {blok.items?.map((item, index) => (
                  <li
                    key={index}
                    className="group relative border-b first:border-t-0 first:pt-0 border-black-50 last:border-b-0 last:pb-0 py-4 lg:py-6"
                  >
                    <button
                      className="flex w-full items-center"
                      onClick={() => toggleAccordion(index)}
                      aria-label={item.title}
                    >
                      <p
                        className={`max-w-[80%] text-midnight mr-auto self-end cursor-pointer h4 text-left mb-0 duration-300 ${
                          selected === index ? "text-orange" : ""
                        }`}
                      >
                        {item.title}
                      </p>
                      <div className="ml-4 transition-transform duration-300 ease-in-out">
                        {selected === index ? (
                          <img
                            src={UpArrowOrange}
                            alt="Toggle Icon"
                            className="w-4 h-4 duration-300 transition-all ease-in-out rotate-[-180deg]"
                          />
                        ) : (
                          <img
                            src={DownArrowNavy}
                            alt="Toggle Icon"
                            className="w-4 h-4 duration-300 transition-all ease-in-out"
                          />
                        )}
                      </div>
                    </button>

                    {/* Accordion Content */}
                    <div
                      className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${
                        selected === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="duration-500 ease-out body-text max-w-[80%] text-midnight mt-4 lg:mt-6 rich-text [&_ul_li::marker]:content-[url('src/static/custom-bullet.svg')]">
                          <ul>
                            {item.bullets?.map((bullet, bulletIndex) => (
                              <li
                                key={bulletIndex}
                                ref={(el) => {
                                  if (!bulletRefs.current[index]) bulletRefs.current[index] = [];
                                  bulletRefs.current[index][bulletIndex] = el;
                                }}
                                className="opacity-0 translate-x-10 transition-all duration-700 ease-out body-text rich-text text-midnight pl-4 [::marker]:content-[url('/static/custom-bullet.svg')]"
                              >
                                {bullet.content}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </SbEditable>
  );
};

export default AccordionAndImage;
